import { Injectable } from '@angular/core';
import { ICallable, IRequestCallback, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';

export interface ICacheOptions {
    ttl?: number;
}

/**
 * Simple caching tool
 *
 * ## Typical usage
 *
 * cacheValue = cache.get('some-name', [arg1, arg2] , function(arg1, arg2) {
 *   ... time consumming calculations  ...
 *   return value;
 * })
 *
 */
@Injectable({
    providedIn: 'root'
})
export class CacheService {
    protected assets: any = {};

    constructor(
        private requestService: RequestService,
        app: App,
        events: Events,
        private timeoutService: TimeoutService
    ) {
        app.cache = this;
        events.on('cms.models.change').subscribe(() => this.clearAll());
    }

    clearAll() {
        this.assets = {};
    }

    clear(key: string | any[]): void {
        this.setAsset(this.getCName(key), undefined);
    }

    /**
     * Sets cache by key and params
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string | any[]}   key   Unique cache id
     * @param  {any}      value
     */
    set(key: string | any[], value: any) {
        return this.setAsset(this.getCName(key), value);
    }

    /**
     * Sets cached value by cache-key
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string} key   Cache key (encoded to json)
     * @param  {any}    value Value to set
     */
    protected setAsset(key: string, value: any, options?: ICacheOptions): any {
        this.assets[key] = value;
        if (options?.ttl) {
            this.timeoutService.setTimeout(() => this.clear(key), options.ttl);
        }
        return value;
    }

    /**
     * Gets cache asset value, if no cache set then generates new value
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string}    key    Unique cache id
     * @param  {any[]}     params Cache params
     * @param  {any[]) =>     any}         callback Generator for cached value if not found
     */
    public get<T = any>(key: any, generator?: () => T, options?: ICacheOptions): T | undefined {
        const cacheName = this.getCName(key);

        if (!this.getAsset(cacheName) && generator) {
            return this.setAsset(cacheName, generator(), options);
        }
        return this.getAsset(cacheName);
    }

    /**
     * Gets cached value by cache key
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string} key
     * @return {any}
     */
    protected getAsset<T = any>(cacheName: string): T | undefined {
        return this.assets[cacheName];
    }

    /**
     * Prepares cache key
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string | any[]} key   Unique cache id
     * @return {string}        cache name
     */
    protected getCName(key: string | any[]): string {
        return JSON.stringify(key);
    }

    /**
     * Handles request result (and request itself) caching by request params
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string | any[]} key     Unique cache key
     * @param  {ICallable} callback Reqeuest
     * @param  {IRequestCallback} onSuccess Success callback
     * @param  {IRequestCallback} onError   Error callback
     */
    cacheRequest<T = any>(
        key: string | any[] | undefined,
        callback: ICallable,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback,
        options?: { noCache: boolean }
    ): Promise<T> {
        const cName = key ? this.getCName(key) : undefined;
        const cNamePromise = cName ? cName + '.promise' : undefined;
        const cachedValue = !cName || options?.noCache ? undefined : this.getAsset(cName);
        let action: ICallable;
        if (cachedValue) {
            action = () => new Promise(resolve => resolve(cachedValue));
        } else {
            const promise =
                (!options?.noCache && cNamePromise && this.getAsset(cNamePromise)) ||
                this.requestService.send(
                    undefined,
                    callback,

                    // once original call finishes we should save final result
                    result => {
                        if (cName) {
                            this.setAsset(cName, result);
                        }
                        if (cNamePromise) {
                            this.clear(cNamePromise);
                        }
                        return result;
                    },

                    // if original call fails we need to clear cashes
                    result => {
                        if (cName) {
                            this.clear(cName);
                        }
                        if (cNamePromise) {
                            this.clear(cNamePromise);
                        }
                        return result;
                    }
                );
            if (cNamePromise) {
                this.setAsset(cNamePromise, promise);
            }
            action = () => promise;
        }

        return this.requestService.send<T>(undefined, action, onSuccess, onError);
    }
}

declare module '@df/core/app' {
    interface App {
        cache: CacheService;
    }
}
